import { useReducer, useRef, useEffect } from 'react';
import authFetch from '../authFetch';

function withHydration(red){
  return (state,action)=>{
    if(action.type==="__HYDRATE__")return {...action.value,_hydrated:true};
    return red(state,action);
  };
}

/* Apply domain migrations to a merged state object */
function migrateState(key,init,merged){
  if(key==="cmd_platform"&&merged.superAdminEmail&&
     (merged.superAdminEmail.endsWith("@command.app")||merged.superAdminEmail.endsWith("@tros.app"))){
    merged.superAdminEmail=init.superAdminEmail;
  }
  /* Populate empty legal pages from seed defaults */
  if(key==="cmd_platform"&&init.legalPages){
    const lp=merged.legalPages||{};
    const dlp=init.legalPages;
    if(!lp.privacyPolicy&&dlp.privacyPolicy) lp.privacyPolicy=dlp.privacyPolicy;
    if(!lp.termsOfService&&dlp.termsOfService) lp.termsOfService=dlp.termsOfService;
    if(!lp.userDataDeletion&&dlp.userDataDeletion) lp.userDataDeletion=dlp.userDataDeletion;
    if(!lp.contactEmail||lp.contactEmail==="privacy@getcommand.app") lp.contactEmail=dlp.contactEmail;
    if(!lp.companyName) lp.companyName=dlp.companyName;
    if(!lp.companyAddress) lp.companyAddress=dlp.companyAddress;
    merged.legalPages=lp;
  }
  return merged;
}

function usePersistedReducer(key,init,red){
  /* ── 1. Boot instantly from localStorage ── */
  const [s,d]=useReducer(withHydration(red),null,()=>{
    try{
      const v=localStorage.getItem(key);
      if(!v)return init;
      return {...migrateState(key,init,{...init,...JSON.parse(v)}),_hydrated:true};
    }catch{return init;}
  });

  /* ── 2. DB always wins on first load — no timestamp comparison.
          DB data is fetched once and applied unconditionally.
          Only after this completes do we allow writes back to DB. ── */
  const dbReady=useRef(false);
  const hydrated=useRef(false); /* true once DB data has been applied to state */
  const dbSnapshot=useRef(null); /* snapshot of what DB had at load time */
  useEffect(()=>{
    let attempt=0;
    const maxRetries=3;
    const tryFetch=()=>{
      authFetch(`/api/data?key=${encodeURIComponent(key)}`)
        .then(r=>r.json())
        .then(({value})=>{
          dbSnapshot.current=value; /* remember DB state for safety checks */
          if(value){
            /* Preserve sensitive fields that are redacted in public reads.
               The API returns "••••" for secret values when unauthenticated.
               We must NOT let redacted values overwrite real credentials
               stored in localStorage from a previous authenticated session. */
            if(key==="cmd_platform"){
              try{
                const ls=localStorage.getItem(key);
                const local=ls?JSON.parse(ls):null;
                if(local){
                  /* Restore notif config secrets if response has redacted values */
                  const nc=value.platformNotifConfig||{};
                  const lnc=local.platformNotifConfig||{};
                  if(nc.emailApiKey==="••••"&&lnc.emailApiKey&&lnc.emailApiKey!=="••••") nc.emailApiKey=lnc.emailApiKey;
                  if(nc.twilioToken==="••••"&&lnc.twilioToken&&lnc.twilioToken!=="••••") nc.twilioToken=lnc.twilioToken;
                  if(!nc.emailApiKey||nc.emailApiKey==="••••") nc.emailApiKey=lnc.emailApiKey||"";
                  value.platformNotifConfig=nc;
                  /* Restore API keys */
                  const ak=value.platformApiKeys||{};
                  const lak=local.platformApiKeys||{};
                  Object.keys(ak).forEach(k=>{if(ak[k]==="••••"&&lak[k]&&lak[k]!=="••••")ak[k]=lak[k];});
                  value.platformApiKeys=ak;
                  /* Restore super admin email */
                  if(!value.superAdminEmail&&local.superAdminEmail) value.superAdminEmail=local.superAdminEmail;
                }
              }catch{}
            }
            const merged=migrateState(key,init,{...init,...value});
            try{localStorage.setItem(key,JSON.stringify(merged));}catch{}
            d({type:"__HYDRATE__",value:merged});
          }
          hydrated.current=true;
          dbReady.current=true;
        })
        .catch((err)=>{
          attempt++;
          if(attempt<maxRetries){
            console.warn(`[usePersistedReducer] DB fetch attempt ${attempt} failed for ${key}, retrying in ${attempt*2}s...`);
            setTimeout(tryFetch,attempt*2000);
          } else {
            /* All retries exhausted. Enable writes ONLY if state was loaded
               from localStorage (has real data). Block if state is just SEED
               defaults (which would wipe real DB data). */
            console.error(`[usePersistedReducer] DB fetch failed after ${maxRetries} attempts for ${key}.`);
            const hasLocalData=!!localStorage.getItem(key);
            if(hasLocalData){
              console.warn("[usePersistedReducer] Enabling writes from localStorage cache.");
              hydrated.current=true;
              dbReady.current=true;
            } else {
              console.error("[usePersistedReducer] No localStorage cache — writes blocked to prevent SEED overwrite.");
            }
          }
        });
    };
    tryFetch();
  },[key]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 3. Write to localStorage AND DB only AFTER hydration completes.
          Before hydration, localStorage writes are blocked to prevent
          stale local data from becoming the source of truth on next load.
          DB writes are also blocked until dbReady (same as before). ── */
  const isMount=useRef(true);
  const pending=useRef(null);
  const timer=useRef(null);
  useEffect(()=>{
    /* Block ALL persistence until DB hydration completes.
       This prevents stale localStorage from poisoning the next session:
       if we wrote pre-hydration state to localStorage, it would be loaded
       on the next page load and could resurrect deleted workspaces. */
    if(!hydrated.current){return;}
    /* Strip internal flags before persisting */
    const toSave={...s};delete toSave._hydrated;
    /* Never let auto-flush write a non-ASCII value into platformApiKeys.
       Real keys are always printable ASCII (sk-ant-…, AIza…, sk_test_…).
       Any non-ASCII value here means the state was hydrated from a
       redacted "••••" response — writing it back would corrupt the DB
       and break every fetch() that uses the key as a header. Restore
       from the DB snapshot if it's clean, otherwise drop to "". The
       server has the same guard; this is defense in depth so we don't
       even spend a round-trip publishing garbage. */
    if(key==="cmd_platform"&&toSave.platformApiKeys&&typeof toSave.platformApiKeys==="object"){
      const snap=dbSnapshot.current?.platformApiKeys||{};
      const isClean=(v)=>typeof v==="string"&&!/[^\x20-\x7E]/.test(v);
      toSave.platformApiKeys=Object.fromEntries(Object.entries(toSave.platformApiKeys).map(([k,v])=>{
        if(typeof v==="string"&&!isClean(v))return[k,isClean(snap[k])?snap[k]:""];
        return[k,v];
      }));
    }
    try{localStorage.setItem(key,JSON.stringify(toSave));}catch{}
    if(isMount.current){isMount.current=false;return;}
    if(!dbReady.current)return;
    /* ── Safety guard: never overwrite populated data with empty defaults ── */
    if(key==="cmd_platform"){
      const dbWsCount=dbSnapshot.current?.workspaces?.length||0;
      if(dbWsCount>0&&(!s.workspaces||s.workspaces.length===0)){
        console.error("[BLOCKED] Refusing to write empty workspaces to DB. DB has",dbWsCount,"workspaces.");
        return;
      }
      /* Protect notif config: don't overwrite real keys with empty or redacted values */
      const dbNc=dbSnapshot.current?.platformNotifConfig;
      const stNc=toSave.platformNotifConfig;
      if(dbNc?.emailApiKey&&dbNc.emailApiKey!=="••••"){
        if(!stNc?.emailApiKey||stNc.emailApiKey==="••••"){
          console.error("[BLOCKED] Refusing to overwrite emailApiKey. Restoring from DB snapshot.");
          toSave.platformNotifConfig={...(stNc||{}),...dbNc};
        }
      }
    }
    pending.current=toSave;
    clearTimeout(timer.current);
    timer.current=setTimeout(()=>{
      const val=pending.current;
      pending.current=null;
      authFetch("/api/data",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({key,value:val}),
        keepalive:true,
      }).catch(e=>console.error("[DB save error]",e));
    },300);
  },[s,key]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 4. Flush pending write immediately if tab is closing ── */
  useEffect(()=>{
    const flush=()=>{
      if(!pending.current)return;
      /* Same safety guards on beforeunload */
      if(key==="cmd_platform"){
        const dbWsCount=dbSnapshot.current?.workspaces?.length||0;
        if(dbWsCount>0&&(!pending.current.workspaces||pending.current.workspaces.length===0)){
          console.error("[BLOCKED] beforeunload: refusing to flush empty workspaces.");
          pending.current=null;
          return;
        }
        /* Protect notif config */
        const dbKey=dbSnapshot.current?.platformNotifConfig?.emailApiKey;
        if(dbKey&&!pending.current.platformNotifConfig?.emailApiKey){
          pending.current.platformNotifConfig={...(pending.current.platformNotifConfig||{}),...dbSnapshot.current.platformNotifConfig};
        }
      }
      /* Strip internal flags */
      delete pending.current._hydrated;
      authFetch("/api/data",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({key,value:pending.current}),
        keepalive:true,
      }).catch(()=>{});
    };
    window.addEventListener("beforeunload",flush);
    return()=>window.removeEventListener("beforeunload",flush);
  },[key]); // eslint-disable-line react-hooks/exhaustive-deps

  return [s,d];
}


export { usePersistedReducer, withHydration, migrateState };
